import { useState, useMemo, useEffect } from 'react';
import { X, Search, MapPin, Navigation, Star, ChevronRight } from 'lucide-react';
import { Club } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

interface ClubSelectorProps {
    clubs: Club[];
    onSelect: (club: Club) => void;
    onClose: () => void;
}

export default function ClubSelector({ clubs, onSelect, onClose }: ClubSelectorProps) {
    const { t } = (useTranslation as any)('matches');
    const { user } = useAuth();
    const [search, setSearch] = useState('');
    const [userLocation, setUserLocation] = useState<{ lat: number, lon: number } | null>(null);
    const [loadingLoc, setLoadingLoc] = useState(false);
    const ALL_COUNTRIES = 'ALL';
    const [selectedCountry, setSelectedCountry] = useState<string>(ALL_COUNTRIES);

    // Initialize location from user profile if available
    useEffect(() => {
        if (!userLocation && user?.departure_lat && user?.departure_lon) {
            setUserLocation({ lat: user.departure_lat, lon: user.departure_lon });
        }
    }, [user, userLocation]);

    // Auto-locate once on mount
    useEffect(() => {
        const timer = setTimeout(() => {
            handleLocateMe({ silent: true });
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    const countries = useMemo(() => {
        const set = new Set(clubs.map(c => c.country || t('create.club.other_countries')));
        return [ALL_COUNTRIES, ...Array.from(set).sort()];
    }, [clubs, t]);

    const groupedClubs = useMemo(() => {
        let result = clubs;

        if (search.trim()) {
            const lower = search.toLowerCase();
            result = result.filter(c =>
                c.name.toLowerCase().includes(lower) ||
                (c.address && c.address.toLowerCase().includes(lower))
            );
        }

        if (userLocation) {
            result = [...result].sort((a, b) => {
                const distA = getDistance(userLocation.lat, userLocation.lon, a.latitude, a.longitude);
                const distB = getDistance(userLocation.lat, userLocation.lon, b.latitude, b.longitude);
                return distA - distB;
            });
        }

        const grouped: Record<string, Club[]> = {};
        const myClub: Club[] = [];
        const nearby: Club[] = [];
        const NEARBY_THRESHOLD = 50;

        result.forEach(club => {
            // Check if it's "My Club"
            if (user?.preferred_club_id && String(club.id) === String(user.preferred_club_id)) {
                myClub.push(club);
                return;
            }

            const country = club.country || t('create.club.other_countries');
            const matchesCountry = selectedCountry === ALL_COUNTRIES || country === selectedCountry;

            if (userLocation && !search.trim()) {
                const dist = getDistance(userLocation.lat, userLocation.lon, club.latitude, club.longitude);
                if (dist <= NEARBY_THRESHOLD) {
                    nearby.push(club);
                }
            }

            if (!matchesCountry) return;

            if (!grouped[country]) grouped[country] = [];
            grouped[country].push(club);
        });

        const finalGrouped: Record<string, Club[]> = {};

        if (myClub.length > 0 && !search.trim()) {
            finalGrouped[t('create.club_selector.my_club', { defaultValue: 'Mon Club' })] = myClub;
        }

        if (nearby.length > 0 && !search.trim()) {
            finalGrouped[t('create.club_selector.nearby')] = nearby;
        }

        const countriesToDisplay = Object.keys(grouped);
        if (userLocation) {
            countriesToDisplay.sort((a, b) => {
                const minDistA = Math.min(...grouped[a].map(c => getDistance(userLocation.lat, userLocation.lon, c.latitude, c.longitude)));
                const minDistB = Math.min(...grouped[b].map(c => getDistance(userLocation.lat, userLocation.lon, c.latitude, c.longitude)));
                return minDistA - minDistB;
            });
        } else {
            countriesToDisplay.sort();
        }

        countriesToDisplay.forEach(key => {
            finalGrouped[key] = grouped[key];
        });

        return finalGrouped;
    }, [clubs, search, userLocation, selectedCountry, user, t]);

    const handleLocateMe = (options?: { silent?: boolean }) => {
        const silent = options?.silent === true;
        if (!silent) setLoadingLoc(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setUserLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude });
                if (!silent) setLoadingLoc(false);
            },
            (err) => {
                console.error(err);
                if (!silent) {
                    setLoadingLoc(false);
                }
            },
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
        );
    };

    function getDistance(lat1: any, lon1: any, lat2: any, lon2: any) {
        const p1 = { lat: parseFloat(lat1), lon: parseFloat(lon1) };
        const p2 = { lat: parseFloat(lat2), lon: parseFloat(lon2) };
        if (isNaN(p1.lat) || isNaN(p1.lon) || isNaN(p2.lat) || isNaN(p2.lon)) return 999999;
        const R = 6371;
        const dLat = (p2.lat - p1.lat) * Math.PI / 180;
        const dLon = (p2.lon - p1.lon) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed inset-0 z-[60] bg-white flex flex-col md:inset-4 md:rounded-3xl md:shadow-2xl md:overflow-hidden"
        >
            {/* Header */}
            <div className="p-4 border-b border-gray-100 flex items-center gap-3 bg-white sticky top-0 z-20">
                <button onClick={onClose} className="p-2 -ml-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors">
                    <X size={24} />
                </button>
                <div className="flex-1 relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors">
                        <Search size={18} strokeWidth={3} />
                    </div>
                    <input
                        type="text"
                        autoFocus
                        placeholder={t('create.club_selector.placeholder')}
                        className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-500 focus:bg-white text-slate-900 pl-12 pr-4 py-3 rounded-2xl outline-none transition-all font-bold placeholder:text-slate-400 shadow-inner"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-white">
                {/* Horizontal Country Tabs */}
                <div className="px-4 py-3 flex gap-2 overflow-x-auto scrollbar-none border-b border-gray-50 flex-shrink-0">
                    {countries.map(country => (
                        <button
                            key={country}
                            onClick={() => setSelectedCountry(country)}
                            className={`px-5 py-2 rounded-full text-xs font-black uppercase tracking-widest whitespace-nowrap transition-all ${selectedCountry === country
                                ? 'bg-slate-900 text-white shadow-lg shadow-slate-200 scale-105'
                                : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                                }`}
                        >
                            {country === ALL_COUNTRIES ? t('create.club.all_countries') : country}
                        </button>
                    ))}
                </div>

                <div className="p-4 space-y-8">
                    {/* Geolocation Hook */}
                    {!search && !userLocation && (
                        <motion.button
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleLocateMe()}
                            disabled={loadingLoc}
                            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-3xl font-black shadow-xl shadow-blue-100"
                        >
                            <div className="flex items-center gap-3 text-sm uppercase tracking-widest">
                                <Navigation size={20} className="fill-white" />
                                {loadingLoc ? t('create.club_selector.locating') : t('create.club_selector.locate_me')}
                            </div>
                            <ChevronRight size={20} />
                        </motion.button>
                    )}

                    {/* Lists */}
                    <AnimatePresence mode="popLayout">
                        {Object.keys(groupedClubs).length === 0 ? (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-center py-20"
                            >
                                <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                                    <Search size={32} />
                                </div>
                                <p className="text-slate-400 font-bold">{t('create.club_selector.no_results')}</p>
                            </motion.div>
                        ) : (
                            Object.entries(groupedClubs).map(([group, groupClubs]: [any, any]) => (
                                <motion.div
                                    key={group}
                                    layout
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="space-y-4"
                                >
                                    <div className="flex items-center gap-3 px-2">
                                        <div className="h-px flex-1 bg-slate-100" />
                                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] whitespace-nowrap">
                                            {group}
                                        </h3>
                                        <div className="h-px flex-1 bg-slate-100" />
                                    </div>
                                    <div className="grid gap-3">
                                        {groupClubs.map((club: any) => {
                                            const isMyClub = user?.preferred_club_id && String(club.id) === String(user.preferred_club_id);
                                            let distDisplay = '';
                                            if (userLocation) {
                                                const d = getDistance(userLocation.lat, userLocation.lon, club.latitude, club.longitude);
                                                distDisplay = d < 1 ? `${(d * 1000).toFixed(0)}m` : `${d.toFixed(1)}km`;
                                            }

                                            return (
                                                <motion.button
                                                    whileTap={{ scale: 0.98 }}
                                                    key={club.id}
                                                    onClick={() => onSelect(club)}
                                                    className={`w-full group bg-white p-4 rounded-3xl border-2 transition-all text-left flex items-center justify-between ${isMyClub
                                                        ? 'border-orange-200 bg-orange-50/20'
                                                        : 'border-slate-50 hover:border-blue-100 hover:bg-blue-50/30'
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-4 overflow-hidden">
                                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg ${isMyClub
                                                            ? 'bg-orange-500 text-white shadow-orange-100'
                                                            : 'bg-white text-slate-400 border border-slate-100 shadow-slate-100 group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600'}`}>
                                                            {isMyClub ? <Star size={20} className="fill-white" /> : <MapPin size={20} />}
                                                        </div>
                                                        <div className="overflow-hidden">
                                                            <div className="font-black text-slate-900 text-sm truncate uppercase tracking-tight">
                                                                {club.name}
                                                            </div>
                                                            <div className="text-[10px] font-bold text-slate-400 flex items-center gap-1.5 mt-0.5 truncate">
                                                                {isMyClub && <span className="text-orange-600 uppercase tracking-tighter mr-1">{t('create.club_selector.my_club_label', { defaultValue: 'Préféré' })}</span>}
                                                                {club.city} • {club.address || t('create.club_selector.unknown_address')}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-4">
                                                        {distDisplay && (
                                                            <div className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-1 rounded-lg uppercase tracking-tight">
                                                                {distDisplay}
                                                            </div>
                                                        )}
                                                        <ChevronRight className="text-slate-200 group-hover:text-slate-400 group-hover:translate-x-1 transition-all" size={16} strokeWidth={3} />
                                                    </div>
                                                </motion.button>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            ))
                        )}
                    </AnimatePresence>
                </div>
            </div>

            <div className="p-4 bg-white border-t border-gray-50 flex justify-center sticky bottom-0 z-20 md:hidden">
                <button
                    onClick={onClose}
                    className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-slate-200"
                >
                    {t('common:actions.close')}
                </button>
            </div>
        </motion.div>
    );
}
